import * as THREE from "three";
import type { Hostile } from "../entities/hostiles";

/**
 * Blocky procedural zombie models.
 *
 * Each zombie owns cloned materials so a hit can flash one mob red without
 * tinting the rest. Geometry is shared and the parts follow the same legs-and-
 * body layout as the passive animals, with the classic outstretched arms.
 */

interface Leg {
  readonly pivot: THREE.Group;
  readonly offset: number;
  readonly isSpider?: boolean;
  readonly baseRotY?: number;
  readonly baseRotZ?: number;
  readonly legIndex?: number;
}

interface MaterialBase {
  readonly material: THREE.MeshBasicMaterial;
  readonly base: THREE.Color;
  readonly unlit?: boolean;
}

interface Visual {
  readonly group: THREE.Group;
  readonly legs: Leg[];
  readonly arms: THREE.Group[];
  readonly materials: MaterialBase[];
  readonly phase: number;
}

const ZOMBIE_SKIN = 0x6f9d5c;
const ZOMBIE_SKIN_DARK = 0x557d47;
const ZOMBIE_SHIRT = 0x2f6f8f;
const ZOMBIE_PANTS = 0x2b3a5a;
const ZOMBIE_HAIR = 0x243d2c;
const EYE = 0x101010;

const SKELETON_BONE = 0xdcdcdc;
const SKELETON_BONE_DARK = 0x9e9e9e;
const SKELETON_SOCKET = 0x141414;
const BOW_WOOD = 0x76532b;
const BOW_STRING = 0xf0f0f0;

const CREEPER_GREEN = 0x4aa63b;
const CREEPER_GREEN_DARK = 0x367f2b;
const CREEPER_GREEN_LIGHT = 0x62c951;
const CREEPER_GREEN_DEEP = 0x275f1e;
const CREEPER_FACE = 0x141414;

const SPIDER_BODY = 0x241c18;
const SPIDER_BODY_DARK = 0x181210;
const SPIDER_BODY_ACCENT = 0x362b24;
const SPIDER_LEG = 0x2b221d;
const SPIDER_EYE_RED = 0xdf1212;

const FLASH = new THREE.Color(0xff4a3d);
const WHITE_FLASH = new THREE.Color(0xffffff);

export class HostileRenderer {
  readonly group = new THREE.Group();

  private readonly boxGeometry = new THREE.BoxGeometry(1, 1, 1);
  private readonly visuals = new Map<number, Visual>();

  constructor(parent: THREE.Object3D) {
    this.group.name = "hostiles";
    parent.add(this.group);
  }

  /** Mirror the simulation into the scene, creating/removing models as needed. */
  sync(hostiles: readonly Hostile[], light: number): void {
    const seen = new Set<number>();
    const brightness = Math.max(0, light);

    for (const hostile of hostiles) {
      seen.add(hostile.id);
      let visual = this.visuals.get(hostile.id);
      if (!visual) {
        visual = this.createVisual(hostile);
        this.visuals.set(hostile.id, visual);
        this.group.add(visual.group);
      }

      const speed = Math.hypot(hostile.velocity.x, hostile.velocity.z);
      const walk = Math.min(1, speed / 2.4);
      const phase = hostile.age * 8 + visual.phase;
      visual.group.position.set(
        hostile.position.x,
        hostile.position.y + Math.abs(Math.sin(phase)) * 0.03 * walk,
        hostile.position.z,
      );
      visual.group.rotation.y = hostile.yaw;

      for (const leg of visual.legs) {
        if (leg.isSpider) {
          const pair = Math.floor((leg.legIndex ?? 0) / 2);
          const isRight = (leg.legIndex ?? 0) % 2 === 1;
          const swing = -Math.cos(phase * 0.8 + pair * (Math.PI / 2)) * 0.35 * walk;
          const lift = Math.abs(Math.sin(phase * 0.4 + pair * (Math.PI / 2))) * 0.35 * walk;
          leg.pivot.rotation.y = (leg.baseRotY ?? 0) + (isRight ? -swing : swing);
          leg.pivot.rotation.z = (leg.baseRotZ ?? 0) + (isRight ? -lift : lift);
        } else {
          leg.pivot.rotation.x = Math.sin(phase + leg.offset) * 0.46 * walk;
        }
      }
      for (let i = 0; i < visual.arms.length; i++) {
        visual.arms[i].rotation.x = -0.08 + Math.sin(phase * 0.5 + i * Math.PI) * 0.05;
      }

      if (hostile.kind === "creeper") {
        const swellNorm = Math.min(1, Math.max(0, hostile.fuse / 1.5));
        const sx = 1.0 + swellNorm * 0.35;
        const sy = 1.0 + swellNorm * 0.45;
        const sz = 1.0 + swellNorm * 0.35;
        visual.group.scale.set(sx, sy, sz);
      } else {
        visual.group.scale.set(1, 1, 1);
      }

      const hurtFlash =
        hostile.hurtFlash > 0 && Math.sin(hostile.age * 55) > -0.25 ? 0.68 : 0;
      const creeperSwellFlash =
        hostile.kind === "creeper" &&
        hostile.fuse > 0 &&
        Math.floor(hostile.fuse * 10) % 2 === 1;

      for (const entry of visual.materials) {
        if (entry.unlit) {
          entry.material.color.copy(entry.base);
        } else {
          entry.material.color.copy(entry.base).multiplyScalar(brightness);
        }
        if (creeperSwellFlash) {
          entry.material.color.lerp(WHITE_FLASH, 0.85);
        } else if (hurtFlash > 0) {
          entry.material.color.lerp(FLASH, hurtFlash);
        }
      }
    }

    for (const [id, visual] of this.visuals) {
      if (seen.has(id)) continue;
      this.disposeVisual(visual);
      this.visuals.delete(id);
    }
  }

  dispose(): void {
    for (const visual of this.visuals.values()) this.disposeVisual(visual);
    this.visuals.clear();
    this.group.removeFromParent();
    this.boxGeometry.dispose();
  }

  private createVisual(hostile: Hostile): Visual {
    const group = new THREE.Group();
    const materials: MaterialBase[] = [];
    const byColor = new Map<number, THREE.MeshBasicMaterial>();

    const material = (hex: number): THREE.MeshBasicMaterial => {
      const existing = byColor.get(hex);
      if (existing) return existing;
      const created = new THREE.MeshBasicMaterial({ color: hex, fog: true });
      byColor.set(hex, created);
      materials.push({ material: created, base: created.color.clone() });
      return created;
    };

    const unlitMaterial = (hex: number): THREE.MeshBasicMaterial => {
      const existing = byColor.get(hex);
      if (existing) return existing;
      const created = new THREE.MeshBasicMaterial({ color: hex, fog: false });
      byColor.set(hex, created);
      materials.push({ material: created, base: created.color.clone(), unlit: true });
      return created;
    };

    const box = (
      parent: THREE.Object3D,
      hex: number,
      x: number,
      y: number,
      z: number,
      sx: number,
      sy: number,
      sz: number,
    ): THREE.Mesh => {
      const mesh = new THREE.Mesh(this.boxGeometry, material(hex));
      mesh.position.set(x, y, z);
      mesh.scale.set(sx, sy, sz);
      parent.add(mesh);
      return mesh;
    };

    const legs: Leg[] = [];
    const leg = (
      hex: number,
      x: number,
      z: number,
      hipY: number,
      width: number,
      length: number,
      offset: number,
    ): void => {
      const pivot = new THREE.Group();
      pivot.position.set(x, hipY, z);
      group.add(pivot);
      const mesh = new THREE.Mesh(this.boxGeometry, material(hex));
      mesh.scale.set(width, length, width);
      mesh.position.y = -length / 2;
      pivot.add(mesh);
      legs.push({ pivot, offset });
    };

    const spiderLeg = (
      pairIdx: number,
      isRight: boolean,
      x: number,
      z: number,
    ): void => {
      const pivot = new THREE.Group();
      pivot.position.set(x, 0.36, z);
      group.add(pivot);

      const legLength = 0.72;
      const legThick = 0.09;
      const mesh = new THREE.Mesh(this.boxGeometry, material(SPIDER_LEG));
      mesh.scale.set(legLength, legThick, legThick);
      mesh.position.set(isRight ? legLength / 2 : -legLength / 2, 0, 0);
      pivot.add(mesh);

      const f = Math.PI / 4;
      const baseRotZ = isRight
        ? (pairIdx === 1 || pairIdx === 2 ? f * 0.74 : f)
        : (pairIdx === 1 || pairIdx === 2 ? -f * 0.74 : -f);

      const f2 = 0.3926991;
      const baseRotY = isRight
        ? (pairIdx === 0 ? -f2 * 2 : pairIdx === 1 ? -f2 : pairIdx === 2 ? f2 : f2 * 2)
        : (pairIdx === 0 ? f2 * 2 : pairIdx === 1 ? f2 : pairIdx === 2 ? -f2 : -f2 * 2);

      pivot.rotation.set(0, baseRotY, baseRotZ);
      legs.push({
        pivot,
        offset: pairIdx * (Math.PI / 2),
        isSpider: true,
        baseRotY,
        baseRotZ,
        legIndex: pairIdx * 2 + (isRight ? 1 : 0),
      });
    };

    const arms: THREE.Group[] = [];

    if (hostile.kind === "spider") {
      // Spider Head: 8x8x8 pixels (0.50 x 0.44 x 0.50)
      box(group, SPIDER_BODY, 0, 0.38, -0.32, 0.50, 0.44, 0.50);
      // Fangs / Pedipalps (2 vertical prongs below face)
      box(group, SPIDER_BODY_DARK, -0.10, 0.18, -0.52, 0.07, 0.12, 0.08);
      box(group, SPIDER_BODY_DARK, 0.10, 0.18, -0.52, 0.07, 0.12, 0.08);

      // Glowing crimson red eyes! (Unlit, glowing in darkness)
      const eyeMesh = (x: number, y: number, z: number, sx: number, sy: number) => {
        const mesh = new THREE.Mesh(this.boxGeometry, unlitMaterial(SPIDER_EYE_RED));
        mesh.position.set(x, y, z);
        mesh.scale.set(sx, sy, 0.02);
        group.add(mesh);
      };
      // Front center prominent eyes
      eyeMesh(-0.11, 0.38, -0.58, 0.09, 0.08);
      eyeMesh(0.11, 0.38, -0.58, 0.09, 0.08);
      // Outer side eyes
      eyeMesh(-0.20, 0.40, -0.575, 0.05, 0.05);
      eyeMesh(0.20, 0.40, -0.575, 0.05, 0.05);
      // Top small eyes
      eyeMesh(-0.06, 0.45, -0.575, 0.04, 0.04);
      eyeMesh(0.06, 0.45, -0.575, 0.04, 0.04);

      // Neck / Thorax: 6x6x6 pixels (0.38 x 0.36 x 0.38)
      box(group, SPIDER_BODY_DARK, 0, 0.36, 0.02, 0.38, 0.36, 0.38);

      // Abdomen / Body: 10x8x12 pixels (0.64 x 0.48 x 0.74)
      box(group, SPIDER_BODY, 0, 0.42, 0.52, 0.64, 0.48, 0.74);
      // Abdomen accent patches / stripes
      box(group, SPIDER_BODY_ACCENT, 0, 0.665, 0.45, 0.28, 0.01, 0.14);
      box(group, SPIDER_BODY_ACCENT, 0, 0.665, 0.62, 0.22, 0.01, 0.12);

      // 8 Jointed Legs (4 pairs, fanned out)
      const legZ = [-0.10, -0.04, 0.04, 0.10];
      for (let pair = 0; pair < 4; pair++) {
        spiderLeg(pair, false, -0.19, legZ[pair]); // Left leg
        spiderLeg(pair, true, 0.19, legZ[pair]);   // Right leg
      }
    } else if (hostile.kind === "creeper") {
      // Creeper torso: 8 wide, 12 tall, 4 deep (0.48 x 0.72 x 0.24)
      box(group, CREEPER_GREEN, 0, 0.74, 0, 0.48, 0.72, 0.24);
      // Camouflage texture patches on torso
      box(group, CREEPER_GREEN_DARK, 0.08, 0.84, 0.121, 0.16, 0.20, 0.01);
      box(group, CREEPER_GREEN_LIGHT, -0.09, 0.64, 0.121, 0.16, 0.20, 0.01);
      box(group, CREEPER_GREEN_DEEP, 0.10, 0.54, 0.121, 0.14, 0.16, 0.01);

      // Creeper head: 8x8x8 (0.50 x 0.50 x 0.50)
      box(group, CREEPER_GREEN, 0, 1.35, 0, 0.50, 0.50, 0.50);
      // Head camouflage patches
      box(group, CREEPER_GREEN_LIGHT, -0.12, 1.50, 0.251, 0.12, 0.10, 0.01);
      box(group, CREEPER_GREEN_DARK, 0.12, 1.50, 0.251, 0.12, 0.10, 0.01);
      box(group, CREEPER_GREEN_DEEP, -0.16, 1.25, 0.251, 0.10, 0.14, 0.01);

      // Iconic sad Creeper face:
      // Eyes (2x2 pixels = 0.09 x 0.09)
      box(group, CREEPER_FACE, -0.10, 1.42, 0.252, 0.09, 0.09, 0.02);
      box(group, CREEPER_FACE, 0.10, 1.42, 0.252, 0.09, 0.09, 0.02);
      // Nose bridge (0.08 x 0.09)
      box(group, CREEPER_FACE, 0, 1.35, 0.252, 0.08, 0.09, 0.02);
      // Downturned mouth: horizontal bridge under nose
      box(group, CREEPER_FACE, 0, 1.28, 0.252, 0.16, 0.07, 0.02);
      // Mouth corners downturned
      box(group, CREEPER_FACE, -0.11, 1.22, 0.252, 0.07, 0.13, 0.02);
      box(group, CREEPER_FACE, 0.11, 1.22, 0.252, 0.07, 0.13, 0.02);

      // 4 Quadruped legs (4x6x4 pixels = 0.24 x 0.38 x 0.24)
      // Leg 1: Back right
      leg(CREEPER_GREEN, -0.13, -0.12, 0.38, 0.22, 0.38, 0);
      // Leg 2: Back left
      leg(CREEPER_GREEN, 0.13, -0.12, 0.38, 0.22, 0.38, Math.PI);
      // Leg 3: Front right
      leg(CREEPER_GREEN, -0.13, 0.12, 0.38, 0.22, 0.38, Math.PI);
      // Leg 4: Front left
      leg(CREEPER_GREEN, 0.13, 0.12, 0.38, 0.22, 0.38, 0);
    } else if (hostile.kind === "skeleton") {
      const arm = (x: number, isRight: boolean): void => {
        const pivot = new THREE.Group();
        pivot.position.set(x, 1.36, 0.02);
        group.add(pivot);
        const mesh = new THREE.Mesh(this.boxGeometry, material(SKELETON_BONE));
        mesh.scale.set(0.12, 0.12, 0.70);
        mesh.position.set(0, -0.02, 0.3);
        pivot.add(mesh);

        // Skeleton holds a bow in its right hand
        if (isRight) {
          const bowPivot = new THREE.Group();
          bowPivot.position.set(-0.06, 0, 0.58);
          // Bow stave
          const bowMesh = new THREE.Mesh(this.boxGeometry, material(BOW_WOOD));
          bowMesh.scale.set(0.06, 0.68, 0.06);
          bowPivot.add(bowMesh);
          // Tips
          const tipT = new THREE.Mesh(this.boxGeometry, material(BOW_WOOD));
          tipT.scale.set(0.06, 0.12, 0.10);
          tipT.position.set(0, 0.36, -0.05);
          bowPivot.add(tipT);
          const tipB = new THREE.Mesh(this.boxGeometry, material(BOW_WOOD));
          tipB.scale.set(0.06, 0.12, 0.10);
          tipB.position.set(0, -0.36, -0.05);
          bowPivot.add(tipB);
          // String
          const strMesh = new THREE.Mesh(this.boxGeometry, material(BOW_STRING));
          strMesh.scale.set(0.02, 0.76, 0.02);
          strMesh.position.set(0, 0, -0.09);
          bowPivot.add(strMesh);

          pivot.add(bowPivot);
        }

        arms.push(pivot);
      };

      // Skeleton pelvis & spine
      box(group, SKELETON_BONE, 0, 0.82, 0, 0.44, 0.10, 0.22);
      box(group, SKELETON_BONE_DARK, 0, 1.15, 0, 0.14, 0.56, 0.14);
      // Ribs
      box(group, SKELETON_BONE, 0, 1.05, 0, 0.48, 0.08, 0.26);
      box(group, SKELETON_BONE, 0, 1.20, 0, 0.50, 0.08, 0.26);
      box(group, SKELETON_BONE, 0, 1.35, 0, 0.52, 0.08, 0.26);

      // Skull
      box(group, SKELETON_BONE, 0, 1.58, 0.02, 0.50, 0.50, 0.50);
      box(group, SKELETON_SOCKET, -0.12, 1.62, 0.271, 0.10, 0.10, 0.02);
      box(group, SKELETON_SOCKET, 0.12, 1.62, 0.271, 0.10, 0.10, 0.02);
      box(group, SKELETON_SOCKET, 0, 1.51, 0.271, 0.06, 0.07, 0.02);
      box(group, SKELETON_SOCKET, 0, 1.41, 0.271, 0.26, 0.05, 0.02);

      arm(-0.32, false);
      arm(0.32, true);

      leg(SKELETON_BONE, -0.15, 0, 0.78, 0.13, 0.78, 0);
      leg(SKELETON_BONE, 0.15, 0, 0.78, 0.13, 0.78, Math.PI);
    } else {
      const arm = (x: number): void => {
        const pivot = new THREE.Group();
        pivot.position.set(x, 1.36, 0.02);
        group.add(pivot);
        const mesh = new THREE.Mesh(this.boxGeometry, material(ZOMBIE_SKIN));
        mesh.scale.set(0.17, 0.17, 0.72);
        mesh.position.set(0, -0.02, 0.3);
        pivot.add(mesh);
        arms.push(pivot);
      };

      // Torso, head and the outstretched arms that make a zombie readable at a
      // glance. Feet sit exactly at the entity's position.
      box(group, ZOMBIE_SHIRT, 0, 1.16, 0, 0.62, 0.74, 0.34);
      box(group, ZOMBIE_SKIN, 0, 1.58, 0.02, 0.52, 0.52, 0.52);
      box(group, ZOMBIE_HAIR, 0, 1.82, 0.02, 0.54, 0.08, 0.54);
      box(group, EYE, -0.12, 1.64, 0.281, 0.09, 0.07, 0.02);
      box(group, EYE, 0.12, 1.64, 0.281, 0.09, 0.07, 0.02);
      box(group, ZOMBIE_SKIN_DARK, 0, 1.49, 0.281, 0.16, 0.04, 0.02);
      arm(-0.4);
      arm(0.4);

      leg(ZOMBIE_PANTS, -0.16, 0, 0.78, 0.21, 0.78, 0);
      leg(ZOMBIE_PANTS, 0.16, 0, 0.78, 0.21, 0.78, Math.PI);
    }

    return {
      group,
      legs,
      arms,
      materials,
      phase: (hostile.id % 7) * 0.9,
    };
  }

  private disposeVisual(visual: Visual): void {
    visual.group.removeFromParent();
    for (const entry of visual.materials) entry.material.dispose();
  }
}
